<template>
  <div class="container mt-5">
    <h4 class="mb-4" style="color: #b03a45;">
      <i class="fas fa-user-edit me-2 text-warning"></i>
      {{ $t("editEmployee.title") }}
    </h4>

    <form @submit.prevent="handleSave">
      <div class="row">
        <!-- Firstname -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("editEmployee.fields.firstname") }}
            </label>
            <input v-model="form.firstname" type="text" class="form-control" />
          </div>
        </div>

        <!-- Lastname -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("editEmployee.fields.lastname") }}
            </label>
            <input v-model="form.lastname" type="text" class="form-control" />
          </div>
        </div>

        <!-- Email -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("editEmployee.fields.email") }}
            </label>
            <input v-model="form.email" type="email" class="form-control" />
          </div>
        </div>

        <!-- Password -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("editEmployee.fields.password") }}
            </label>
            <div class="position-relative">
              <input 
                v-model="form.password" 
                :type="showPassword ? 'text' : 'password'" 
                class="form-control" 
              />
              <button
                type="button"
                class="btn position-absolute end-0 top-50 translate-middle-y me-2"
                style="background: none; border: none; color: #6c757d; padding: 0; z-index: 10;"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Confirm Password -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("editEmployee.fields.confirmPassword") }}
            </label>
            <div class="position-relative">
              <input 
                v-model="form.password_confirmation" 
                :type="showConfirmPassword ? 'text' : 'password'" 
                class="form-control" 
              />
              <button
                type="button"
                class="btn position-absolute end-0 top-50 translate-middle-y me-2"
                style="background: none; border: none; color: #6c757d; padding: 0; z-index: 10;"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Rôle : on travaille avec le NOM (role) -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("editEmployee.fields.role") }}
            </label>
            <select v-model="form.role" class="form-select">
              <option disabled value="">{{ $t("editEmployee.placeholders.role") }}</option>
              <option
                v-for="role in roles"
                :key="role.id"
                :value="role.name"
              >
                {{ role.name }}
              </option>
            </select>

            <small v-if="!loadingRoles && roles.length === 0" class="text-muted">
              {{ $t("editEmployee.noRolesFound") }}
            </small>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="d-flex justify-content-end gap-3 mt-4">
        <button
          type="submit"
          class="btn d-flex align-items-center justify-content-center"
          style="background-color: #b03a45; color: white; width: 50px; height: 45px; border-radius: 12px;"
        >
          <i class="fas fa-save"></i>
        </button>

        <button
          type="button"
          class="btn d-flex align-items-center justify-content-center"
          style="background-color: #b03a45; color: white; width: 50px; height: 45px; border-radius: 12px;"
          @click="goToNext"
        >
          <i class="fas fa-arrow-right"></i>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const userId = route.query.user_id

// --- Données formulaire (role = NOM attendu par le back)
const form = ref({
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  password_confirmation: '',
  role: '' // ex: "employee"
})

const showPassword = ref(false)
const showConfirmPassword = ref(false)

// --- Rôles dynamiques
const roles = ref([])
const loadingRoles = ref(true)

// Efface la confirmation si le mot de passe est vidé
watch(() => form.value.password, (val) => {
  if (!val) form.value.password_confirmation = ''
})

// Charger la liste des rôles
const fetchRoles = async () => {
  try {
    const res = await axios.get('/roles')
    roles.value = res.data.data || res.data
  } catch (error) {
    toast.error(t('editEmployee.messages.roleLoadError'))
    console.error('Erreur chargement rôles :', error)
  } finally {
    loadingRoles.value = false
  }
}

// Charger infos utilisateur
const fetchUser = async () => {
  try {
    const { data } = await axios.get(`/getUser/${userId}`)
    form.value.firstname = data.user.firstname
    form.value.lastname  = data.user.lastname
    form.value.email     = data.user.email
    // Le backend renvoie: { user: {...}, role: "<nom_du_role>" }
    form.value.role      = data.role || ''
  } catch (error) {
    toast.error(t('editEmployee.messages.loadError'))
    console.error(error)
  }
}

// Sauvegarde
const handleSave = async () => {
  if (form.value.password && form.value.password !== form.value.password_confirmation) {
    toast.error(t('editEmployee.messages.passwordMismatch'))
    return
  }

  try {
    const payload = {
      firstname: form.value.firstname,
      lastname: form.value.lastname,
      email: form.value.email,
      role: form.value.role // **nom du rôle**
    }

    if (form.value.password) {
      payload.password = form.value.password
      payload.password_confirmation = form.value.password_confirmation
    }

    await axios.put(`/updateUser/${userId}`, payload)
    toast.success(t('editEmployee.messages.success'))
  } catch (error) {
    console.error(error)
    if (error.response?.status === 422) {
      const errors = error.response.data.errors
      const firstError = Object.values(errors)?.[0]?.[0] || t('editEmployee.messages.updateFailed')
      toast.error(firstError)
    } else {
      toast.error(t('editEmployee.messages.updateFailed'))
    }
  }
}

// Étape suivante
const goToNext = () => {
  router.push({ path: '/employee/information/edit', query: { user_id: userId } })
}

// Init
onMounted(() => {
  fetchRoles()
  fetchUser()
})
</script>

<style scoped>
select.form-select { cursor: pointer; }
</style>
